import { z } from 'zod';
/**
 * Контракты списков стран (`/api/ads/country-lists`) — шлюз admin-api к
 * глобальным спискам auto-ads (`/internal/scale/country-lists`). Списки
 * общие с правилами SCALE: правка здесь меняет и гео будущих клонов SCALE.
 *
 * Границы — те же, что у auto-ads (`scale-country-lists`): коды
 * ISO 3166-1 alpha-2 (+ XK) в ВЕРХНЕМ регистре без повторов (auto-ads
 * отвечает 400 `country-unknown` на выдуманный код), ничего не
 * нормализуется по дороге (форма сама приводит к верхнему регистру).
 *
 * Тела запросов — `.strict()`; ответы мягкие, `note` — `.nullable().default(null)`.
 */
export const ADS_COUNTRY_LIST_NAME_MAX = 80;
export const ADS_COUNTRY_LIST_COUNTRIES_MAX = 250;
export const ADS_COUNTRY_LIST_NOTE_MAX = 500;
/**
 * Коды стран, которые принимает Meta (`geo_locations.countries`): ISO 3166-1 alpha-2 (249) + `XK`
 * (Косово) — 250 кодов, ровно набор пикера админки (`ui-domain/src/lib/country.ts` →
 * `ALL_COUNTRY_CODES`, там он вычисляется из CLDR; здесь — статическая копия, чтобы решение
 * сервера не зависело от сборки ICU). Та же копия — в auto-ads (`src/shared/meta/iso-countries.ts`).
 * Нет: `UK` (это `GB`), `EU`, `EL`, `SU`, `AC`, `TA`… — Meta их отвергает.
 */
export const ADS_COUNTRY_CODES = Object.freeze([
    'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
    'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
    'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
    'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
    'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
    'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
    'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
    'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
    'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
    'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
    'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
    'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
    'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
    'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
    'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
    'VN', 'VU', 'WF', 'WS', 'XK', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
]); // prettier-ignore
const ADS_COUNTRY_SET = new Set(ADS_COUNTRY_CODES);
/** Настоящий код страны (только ВЕРХНИЙ регистр — ничего не нормализуется). */
export const isAdsCountryCode = (code) => ADS_COUNTRY_SET.has(code);
export const AdsCountryCode = z
    .string()
    .regex(/^[A-Z]{2}$/, {
    message: 'Страна — двухбуквенный код ISO заглавными, например DE',
})
    .refine(isAdsCountryCode, {
    message: 'Страна не найдена в справочнике стран — выберите страну из списка (Великобритания — GB)',
});
const Countries = z
    .array(AdsCountryCode)
    .min(1, { message: 'Нужна хотя бы одна страна' })
    .max(ADS_COUNTRY_LIST_COUNTRIES_MAX, {
    message: `Не больше ${ADS_COUNTRY_LIST_COUNTRIES_MAX} стран`,
})
    .refine((codes) => new Set(codes).size === codes.length, {
    message: 'Страны не должны повторяться',
});
const countryListInputShape = {
    name: z
        .string()
        .trim()
        .min(1, { message: 'Название обязательно' })
        .max(ADS_COUNTRY_LIST_NAME_MAX, {
        message: `Название — не длиннее ${ADS_COUNTRY_LIST_NAME_MAX} символов`,
    }),
    countries: Countries,
    note: z
        .string()
        .max(ADS_COUNTRY_LIST_NOTE_MAX, {
        message: `Примечание — не длиннее ${ADS_COUNTRY_LIST_NOTE_MAX} символов`,
    })
        .optional(),
};
export const AdsCountryListInput = z.object(countryListInputShape).strict();
/** PUT частичный: те же поля, каждое необязательно; пустой патч — 400. */
export const AdsCountryListUpdate = z
    .object(countryListInputShape)
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Нечего обновлять' });
export const AdsCountryList = z.object({
    id: z.string().min(1),
    name: z.string(),
    countries: z.array(z.string()),
    note: z.string().nullable().default(null),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const AdsCountryListResponse = z.object({ items: z.array(AdsCountryList) });
//# sourceMappingURL=ads-lists.js.map